export const FACTIONS = ["EFA", "REDCAT", "GREEN", "BLUE"] as const;
export type Faction = (typeof FACTIONS)[number];
export type Terrain = "VOID" | "MEADOW" | "DESERT" | "FOREST" | "FOREST_GREEN" | "WATER" | "MOUNTAIN" | "MOUNTAIN_GREEN" | "FORD";
export type Category = "BUILDING" | "INFANTRY" | "WHEELED" | "ARMORED" | "AIR";
export type TurnPhase = "ACTIVATION" | "PRODUCTION" | "VICTORY";
export type GameMode = "TRAINING" | "SKIRMISH" | "CUSTOM" | "SOLO" | "CUSTOM_CAMPAIGN";
export type Orientation = "FLAT_TOP" | "POINTY_TOP";

export const PIECE_TYPES = [
  "CONSTRUCTION_YARD", "POWER_PLANT", "REFINERY", "BARRACKS", "FACTORY",
  "HELIPAD", "UPLINK", "MG_TOWER", "RAK_TOWER", "MG_INFANTRY",
  "RAK_INFANTRY", "ING_INFANTRY", "MG_BUGGY", "HARVESTER", "TANK", "MCV",
  "APC", "ARTILLERY", "MOBILE_FORTRESS", "DROPSHIP", "FIGHTER", "BOMBER",
] as const;
export type PieceType = (typeof PIECE_TYPES)[number];

export interface PieceDefinition {
  type: PieceType;
  category: Category;
  maxCount: number;
  tech: number;
  cost: number;
  productionTime: number;
  speed: number;
  minRange: number;
  maxRange: number;
  consumesEnergy: boolean;
  shield: boolean;
  notInForest: boolean;
  targets: readonly Category[];
  asset: string;
}

export interface GamePiece {
  id: number;
  type: PieceType;
  owner: Faction;
  col: number;
  row: number;
  activated: boolean;
  movementUsed: boolean;
  movedThisActivation?: boolean;
  actionUsed: boolean;
  ammoLoaded: boolean;
  carryingOre: boolean;
  suppressed: boolean;
  entrenched: boolean;
  tilted: boolean;
  retaliatedThisTurn: boolean;
  hitsThisTurn: number;
  capturedRound: number;
  facing: number;
  outputCount: number;
  cargo: PieceType[];
  cargoBoardedThisTurn: number;
}

export interface ProductionItem {
  id: number;
  type: PieceType;
  remaining: number;
  orderedThisTurn?: boolean;
}

export interface PlayerStats {
  oreHarvested: number;
  creditsSpent: number;
  unitsTrained: number;
  buildingsBuilt: number;
  unitsDestroyed: number;
  buildingsDestroyed: number;
  unitsLost: number;
  buildingsLost: number;
  buildingsCaptured: number;
}

export interface PlayerState extends PlayerStats {
  faction: Faction;
  name: string;
  credits: number;
  orbitalCooldown: number;
  queue: ProductionItem[];
}

export interface MissionState {
  mode: "" | "SOLO_MISSION_01" | "SOLO_MISSION_02";
  capturedCivilBuildings: number;
  spawnWave: number;
  triggerRound: number;
  diceRolls: number[];
  triggerReport: string;
  specialCol: number;
  specialRow: number;
  empAvailable: boolean;
  empUsed: boolean;
}

export interface GameState {
  schemaVersion: 1;
  width: number;
  height: number;
  orientation: Orientation;
  terrain: Terrain[][];
  terrainVariants: number[][];
  bridges: number[][];
  riverStyles: number[][];
  bridgeRotations: number[][];
  ore: boolean[][];
  civilBuilding: boolean[][];
  civilOwner: (Faction | null)[][];
  pieces: GamePiece[];
  players: Record<Faction, PlayerState>;
  activePlayers: Record<Faction, boolean>;
  aiPlayers: Record<Faction, boolean>;
  teams: Record<Faction, number>;
  startPositions: Record<Faction, number>;
  turnOrder: Faction[];
  startingDiceRolls: Record<Faction, number>;
  humanFaction: Faction;
  round: number;
  currentFaction: Faction;
  roundStarter: Faction;
  phase: TurnPhase;
  winner: Faction | null;
  nextPieceId: number;
  nextQueueId: number;
  scenarioId: string;
  scenarioName: string;
  objective: string;
  event: string;
  mode: GameMode;
  techLevel: number;
  seed: number;
  rngState: number;
  mission: MissionState;
  mapStartLocations?: Array<{ position: number; col: number; row: number }>;
  localSpectator?: boolean;
  noProgressTurns?: number;
  progressSignature?: string;
  participants?: Record<Faction, boolean>;
  pendingRetaliation?: {
    attackerId: number;
    targetId: number;
    attackerFaction: Faction;
    targetFaction: Faction;
    decision?: boolean;
  };
}

export interface ActionResult { success: boolean; message: string }
export interface ScenarioJson {
  id: string;
  name: string;
  mode?: string;
  ruleset?: string;
  objective?: string;
  orientation?: Orientation;
  randomStart?: boolean;
  credits?: Partial<Record<Faction | "EFA" | "REDCAT", number>>;
  terrain: string[];
  bridges?: Array<[number, number, boolean?, number?]>;
  ore?: Array<[number, number]>;
  pieces: Array<{ type: PieceType; owner: Faction; col: number; row: number }>;
}

export interface ReplayFrame { at: number; event: string; state: GameState }
export interface ReplayRecord {
  schemaVersion: 1;
  id: string;
  createdAt: string;
  updatedAt?: string;
  durationMs?: number;
  completed: boolean;
  mode: GameMode;
  scenarioId: string;
  scenarioName: string;
  winner: Faction | null;
  rounds: number;
  players: Array<{ faction: Faction; name: string; team: number }>;
  frames: ReplayFrame[];
}

export interface StoredMap {
  schemaVersion: 1;
  id: string;
  name: string;
  width: number;
  height: number;
  baseBiome: "MEADOW" | "DESERT";
  terrain: Terrain[][];
  terrainVariants?: number[][];
  bridges: number[][];
  riverStyles: number[][];
  bridgeRotations: number[][];
  ore: boolean[][];
  pieces: Array<Pick<GamePiece, "type" | "owner" | "col" | "row" | "facing">>;
  startPositions: Array<{ faction?: Faction; position?: number; col: number; row: number }>;
}

export const ok = (message: string): ActionResult => ({ success: true, message });
export const fail = (message: string): ActionResult => ({ success: false, message });
